import { dash } from "../../wallets/lib/format";

/** Compact dollars for dense cells: $1.2M, $84K, $940. */
export function usd(v: number | null | undefined): string {
	if (v === null || v === undefined || !Number.isFinite(v)) return dash;
	const a = Math.abs(v);
	const s = v < 0 ? "-" : "";
	if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
	if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(a >= 1e7 ? 1 : 2)}M`;
	if (a >= 1e4) return `${s}$${(a / 1e3).toFixed(0)}K`;
	if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(1)}K`;
	return `${s}$${a.toFixed(0)}`;
}

/** Price as cents with one decimal below 10¢ or above 90¢, where it matters: 96.4¢, 3.6¢, 41¢. */
export function cents(p: number | null | undefined): string {
	if (p === null || p === undefined || !Number.isFinite(p)) return dash;
	const c = p * 100;
	return `${c < 10 || c > 90 ? c.toFixed(1) : c.toFixed(0)}¢`;
}

/** Signed change in cents: +12.0¢. */
/** False when the browser refuses, as it can outside a secure page or without focus. */
export async function copyText(text: string): Promise<boolean> {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
}

export function centsDelta(d: number | null | undefined): string {
	if (d === null || d === undefined || !Number.isFinite(d)) return dash;
	const c = d * 100;
	if (Math.abs(c) < 0.05) return "0.0¢";
	return `${c > 0 ? "+" : ""}${c.toFixed(1)}¢`;
}

export function pct0(v: number | null | undefined): string {
	return v === null || v === undefined || !Number.isFinite(v) ? dash : `${v.toFixed(0)}%`;
}

/** Signed percentage from a value already in percent: +57.6%. */
export function pctSigned(v: number | null | undefined, digits = 1): string {
	if (v === null || v === undefined || !Number.isFinite(v)) return dash;
	return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
}

export function count(v: number | null | undefined): string {
	if (v === null || v === undefined || !Number.isFinite(v)) return dash;
	return v.toLocaleString("en-US");
}

/**
 * Invisible text as wide as `n` monospace characters, wrapping like words do. Holds a header's
 * place until its figures load, so the rows below do not move. No digits, nothing to misread.
 */
export const blankText = (n: number): string => "\u00A0 ".repeat(Math.ceil(n / 2));

const pad = (n: number) => String(n).padStart(2, "0");

/** Short relative age from a unix-seconds timestamp. */
export function ago(tsSeconds: number, nowMs = Date.now()): string {
	if (!tsSeconds) return dash;
	const s = Math.max(0, Math.round(nowMs / 1000 - tsSeconds));
	if (s < 60) return `${s}s`;
	if (s < 3600) return `${Math.floor(s / 60)}m`;
	if (s < 86400) return `${Math.floor(s / 3600)}h`;
	return `${Math.floor(s / 86400)}d`;
}

/** MM-DD HH:MM in the viewer's timezone. */
export function stamp(tsSeconds: number): string {
	if (!tsSeconds) return dash;
	const d = new Date(tsSeconds * 1000);
	return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** YYYY-MM-DD from an ISO string, or a dash when missing or invalid. */
export function isoDay(iso: string | null | undefined): string {
	if (!iso) return dash;
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? dash : d.toISOString().slice(0, 10);
}

export function clock(ms: number): string {
	const d = new Date(ms);
	return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function hoursSpan(h: number | null | undefined): string {
	if (h === null || h === undefined || !Number.isFinite(h)) return dash;
	if (h < 1) return `${Math.round(h * 60)}m`;
	if (h < 48) return `${h.toFixed(0)}h`;
	return `${(h / 24).toFixed(0)}d`;
}

export const toneClass = (v: number | null | undefined): string => {
	if (v === null || v === undefined || !Number.isFinite(v) || v === 0) return "text-dim";
	return v > 0 ? "text-up" : "text-dn";
};
